import React, { useState } from 'react';
import { nowSeconds } from '../platform/events/operationEvents';
import { getCurrentRole } from '../shared/permissions';
import {
  getQBODates, getQBOInvoiceNumber, downloadQBInvoice,
  buildQBOInvoiceCsv, qboExportNotes, QB_FORMATS, DEFAULT_QB_FORMAT,
} from '../platform/export/qboExport';
import './EstimatePanel.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeId(prefix = 'el') {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function fmtMoney(n) {
  const v = parseFloat(n);
  if (isNaN(v)) return '—';
  return `$${v.toFixed(2)}`;
}

function fmtDateLong(ts) {
  const ms = !ts ? Date.now() : (ts < 10_000_000_000 ? ts * 1000 : ts);
  return new Date(ms).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtDateShort(ts) {
  if (!ts) return '—';
  const ms = ts < 10_000_000_000 ? ts * 1000 : ts;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateIso(ts = Date.now()) {
  const ms = ts && ts < 10_000_000_000 ? ts * 1000 : ts;
  return new Date(ms).toISOString().slice(0, 10);
}

function clean(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(clean);
  const { __typename, ...rest } = obj;
  return Object.fromEntries(Object.entries(rest).map(([k, v]) => [k, clean(v)]));
}

function calcLaborAmount(hours, rate, flatPrice, useFlatRate) {
  if (useFlatRate || (hours == null && rate == null)) return parseFloat(flatPrice) || 0;
  const h = parseFloat(hours);
  const r = parseFloat(rate);
  return (!isNaN(h) && !isNaN(r)) ? h * r : 0;
}

// Per-operation labor/parts/total (one EstimateLine = one operation)
export function calcLineTotals(line, parts) {
  const laborLines  = line?.laborLines  || [];
  const partLineIds = line?.partLineIds || [];
  const labor = laborLines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const laborHours = laborLines.reduce((s, l) => s + (parseFloat(l.hours) || 0), 0);
  const partsTotal = (parts || [])
    .filter(p => partLineIds.includes(p.id))
    .reduce((s, p) => s + (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1), 0);
  return { labor, laborHours, parts: partsTotal, total: labor + partsTotal };
}

export function calcTotals(estimate, parts) {
  const lines = (estimate.lines || []);

  let laborSubtotal = 0;
  let partsSubtotal = 0;
  let taxableBase   = 0; // tax-exempt operations (state inspections…) excluded

  for (const line of lines) {
    const lt = calcLineTotals(line, parts);
    laborSubtotal += lt.labor;
    partsSubtotal += lt.parts;
    if (!line.taxExempt) taxableBase += lt.total;
  }

  let shopSupplies = 0;
  if (estimate.shopSuppliesPct) {
    shopSupplies = laborSubtotal * (parseFloat(estimate.shopSuppliesPct) / 100);
  } else {
    shopSupplies = parseFloat(estimate.shopSupplies) || 0;
  }

  const subtotal = laborSubtotal + partsSubtotal + shopSupplies;
  const taxRate  = parseFloat(estimate.taxRate) || 0;
  const tax      = (taxableBase + shopSupplies) * (taxRate / 100);
  const total    = subtotal + tax;

  return { laborSubtotal, partsSubtotal, shopSupplies, subtotal, tax, total };
}

export function emptyEstimateLine() {
  return {
    title: '',
    complaint: '',
    cause: '',
    correction: '',
    laborLines: [],
    partLineIds: [],
    taxExempt: false,
  };
}

function makePublicToken() {
  const bytes = new Uint8Array(16);
  window.crypto?.getRandomValues?.(bytes);
  const random = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return random || `${Date.now()}${Math.random().toString(36).slice(2)}`;
}

function publicAppOrigin() {
  const configured = process.env.REACT_APP_PUBLIC_APP_URL || window.localStorage?.getItem('rekon_public_app_url');
  return String(configured || window.location.origin).replace(/\/+$/, '');
}

function estimateApprovalUrl(token) {
  return `${publicAppOrigin()}/estimate/${encodeURIComponent(token)}`;
}

function makeActivity(type, text, truck) {
  return {
    id: makeId('act'),
    type,
    text,
    author: getCurrentRole(),
    timestamp: nowSeconds(),
    customerFacing: type === 'authorization',
    stage: truck?.status || null,
  };
}

function unitInfo(truck = {}) {
  return [
    truck.truckNumber ? `Unit ${truck.truckNumber}` : '',
    [truck.year, truck.make, truck.model].filter(Boolean).join(' '),
    truck.vin ? `VIN ${truck.vin}` : '',
  ].filter(Boolean).join(' | ');
}

// The shop's remit-to block for the top of the customer-facing paper, as
// printable lines. Empty until Settings → Business is filled in.
function remitToLines(settings) {
  const b = settings?.business || {};
  const cityLine = [b.city, [b.state, b.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean).join(', ');
  const contact = [b.phone, b.email].filter(Boolean).join(' · ');
  return [b.addressLine1, b.addressLine2, cityLine, contact, b.website]
    .map(l => (l || '').trim())
    .filter(Boolean);
}

function plainTextEstimate(estimate, line0, parts, totals, truck, settings, approvalUrl = null, invoiceMode = false) {
  // Customer-facing paper carries the SHOP's name, never the software's
  const shopName = settings?.business?.shopName || settings?.business?.companyName || '';
  const shopPhone = settings?.business?.phone || settings?.business?.shopPhone || truck.shopPhone || 'the shop';
  const docNumber = invoiceMode ? getQBOInvoiceNumber(estimate, truck) : `RO ${truck.repairOrder}`;
  const header = invoiceMode ? `Invoice ${docNumber}` : `Estimate for ${truck.companyName} - RO ${truck.repairOrder}`;
  const ops = estimate.lines || [];
  const multiOp = ops.length > 1;
  const lines = [
    shopName,
    ...remitToLines(settings),
    '',
    header,
    `RO: ${truck.repairOrder}`,
    `Unit: ${unitInfo(truck) || 'N/A'}`,
  ];
  ops.forEach((op, i) => {
    const opLabor = op.laborLines || [];
    const opParts = (parts || []).filter(p => (op.partLineIds || []).includes(p.id));
    if (!op.title && !op.complaint && opLabor.length === 0 && opParts.length === 0) return;
    lines.push('');
    if (multiOp || op.title) {
      lines.push(`=== Operation ${multiOp ? `${i + 1}` : ''}${op.title ? ` — ${op.title}` : ''}${op.taxExempt ? ' (tax exempt)' : ''} ===`);
    }
    if (op.complaint)  lines.push('Complaint:',  op.complaint);
    if (op.cause)      lines.push('Cause:',      op.cause);
    if (op.correction) lines.push('Correction:', op.correction);
    if (opLabor.length)  lines.push('Labor:',  ...opLabor.map(l => `- ${l.description}: ${fmtMoney(l.amount)}`));
    if (opParts.length)  lines.push('Parts:',  ...opParts.map(p => `- ${p.description}${p.partNumber ? ` #${p.partNumber}` : ''} x${p.quantity || 1}: ${fmtMoney((parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1))}`));
  });
  lines.push('', `Total: ${fmtMoney(totals.total)}`);
  if (approvalUrl) {
    lines.push('', 'To approve or decline, visit:', approvalUrl, `Or reply to this email or call us at ${shopPhone}.`);
  } else if (invoiceMode) {
    lines.push('', 'Please call us with any payment questions.', 'A printed copy is available upon request.');
  }
  return lines.join('\n');
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const STATUS_CONFIG = {
  draft:    { label: 'Draft',    color: '#64748b', bg: '#f1f5f9' },
  sent:     { label: 'Sent',     color: '#2563eb', bg: '#eff6ff' },
  approved: { label: 'Approved', color: '#16a34a', bg: '#dcfce7' },
  declined: { label: 'Declined', color: '#dc2626', bg: '#fee2e2' },
  invoiced: { label: 'Invoiced', color: '#7c3aed', bg: '#f5f3ff' },
  paid:     { label: 'Paid',     color: '#15803d', bg: '#dcfce7' },
};

const PAYMENT_METHODS = [
  { value: 'check',         label: 'Check' },
  { value: 'card',          label: 'Card' },
  { value: 'cash',          label: 'Cash' },
  { value: 'ach',           label: 'ACH / Wire' },
  { value: 'fleet_account', label: 'Fleet Account' },
  { value: 'other',         label: 'Other' },
];

const APPROVAL_METHODS = [
  { value: 'phone',      label: 'Phone' },
  { value: 'in_person',  label: 'In Person' },
  { value: 'email',      label: 'Email' },
  { value: 'text',       label: 'Text' },
  { value: 'email_link', label: 'Email Link' },
  { value: 'other',      label: 'Other' },
];

const EMPTY_LABOR = {
  description: '', hours: '', rate: '', flatPrice: '',
  techName: '', notes: '', useFlatRate: false, catalogItemId: '',
};

// ─── LaborForm ────────────────────────────────────────────────────────────────

function LaborForm({ form, setForm, onSubmit, onCancel, defaultRate, isEdit, catalogItems = [] }) {
  const amount = calcLaborAmount(form.hours, form.rate, form.flatPrice, form.useFlatRate);

  // Menu pricing: pick a catalog job and its hours/rate (or flat price) land in
  // the form, still editable before it goes on the ticket.
  function applyCatalogItem(id) {
    const item = catalogItems.find(i => i.id === id);
    if (!item) { setForm(f => ({ ...f, catalogItemId: '' })); return; }
    const flat = item.rateType === 'flat';
    setForm(f => ({
      ...f,
      catalogItemId: item.id,
      description: item.name || f.description,
      useFlatRate: flat,
      hours:     flat ? '' : (item.laborHours != null ? String(item.laborHours) : ''),
      rate:      flat ? '' : String(item.laborRate ?? defaultRate),
      flatPrice: flat ? (item.flatPrice != null ? String(item.flatPrice) : '') : '',
    }));
  }

  return (
    <form className="ep-labor-form" onSubmit={onSubmit}>
      <div className="ep-labor-form-top">
        {catalogItems.length > 0 && (
          <select
            className="ep-input ep-input--catalog"
            value={form.catalogItemId || ''}
            onChange={e => applyCatalogItem(e.target.value)}
          >
            <option value="">From catalog…</option>
            {catalogItems.map(i => (
              <option key={i.id} value={i.id}>
                {i.category ? `${i.category} — ${i.name}` : i.name}
              </option>
            ))}
          </select>
        )}
        <input
          className="ep-input ep-input--desc"
          placeholder="Description *"
          value={form.description}
          onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          required
        />
        <label className="ep-flat-toggle">
          <input type="checkbox" checked={form.useFlatRate}
            onChange={e => setForm(f => ({ ...f, useFlatRate: e.target.checked }))} />
          Flat rate
        </label>
      </div>
      <div className="ep-labor-form-bottom">
        {form.useFlatRate ? (
          <label className="ep-input-group ep-input-group--inline">
            {/* No min — a negative flat price IS a discount line, and editing
                an existing discount has to be able to stay negative. */}
            <span>Flat Price</span>
            <input className="ep-input ep-input--money" type="number" step="0.01"
              placeholder="0.00" value={form.flatPrice}
              onChange={e => setForm(f => ({ ...f, flatPrice: e.target.value }))} />
          </label>
        ) : (
          <>
            <label className="ep-input-group ep-input-group--inline">
              <span>Hours</span>
              <input className="ep-input ep-input--num" type="number" min="0" step="0.25"
                placeholder="0.0" value={form.hours}
                onChange={e => setForm(f => ({ ...f, hours: e.target.value }))} />
            </label>
            <label className="ep-input-group ep-input-group--inline">
              <span>Rate $/hr</span>
              <input className="ep-input ep-input--money" type="number" min="0" step="1"
                placeholder={String(defaultRate)} value={form.rate}
                onChange={e => setForm(f => ({ ...f, rate: e.target.value }))} />
            </label>
          </>
        )}
        <label className="ep-input-group ep-input-group--inline">
          <span>Tech</span>
          <input className="ep-input ep-input--sm" placeholder="optional"
            value={form.techName}
            onChange={e => setForm(f => ({ ...f, techName: e.target.value }))} />
        </label>
        <div className="ep-labor-amount-preview">
          <span className="ep-labor-amount-label">Amt</span>
          <span className="ep-labor-amount-val">{fmtMoney(amount)}</span>
        </div>
        <button type="submit" className="ep-btn ep-btn--primary ep-btn--sm">
          {isEdit ? 'Update' : '+ Add'}
        </button>
        <button type="button" className="ep-btn ep-btn--ghost ep-btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Builder mode ─────────────────────────────────────────────────────────────

function BuilderMode({
  draft, line0, lines, lineIdx, onSelectLine, onAddOperation, onRemoveOperation,
  parts, totals, findings, repairs, canEdit,
  canAct,
  defaultLaborRate, catalogItems, addLaborOpen, setAddLaborOpen, laborForm, setLaborForm,
  editLaborId, setEditLaborId, onUpdateLine, onUpdateDraft,
  onAddLabor, onDeleteLabor, onEditLabor, onTogglePart, onSave, saving,
  findingOpen, setFindingOpen, repairOpen, setRepairOpen,
  approvalMode, setApprovalMode, approvalForm, setApprovalForm,
  declineMode, setDeclineMode, declineReason, setDeclineReason,
  paymentMode, setPaymentMode, paymentForm, setPaymentForm, onRecordPayment,
  sentLink, onRequestApproval, onApprove, onDecline, onCreateInvoice,
  onFinalizeInvoice, onRevertInvoice, onEmailInvoice, onExportQbo, exportOpen,
  discountMode, setDiscountMode, discountForm, setDiscountForm, onSaveDiscount,
  onDownloadQbo, onCopyQbo, exportNotes = [],
}) {
  const laborLines  = line0.laborLines  || [];
  const sc = STATUS_CONFIG[draft.status] || STATUS_CONFIG.draft;
  const paidAmt = (draft.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const remainingAmt = Math.max(0, totals.total - paidAmt);

  return (
    <div className="ep-builder">
      <div className="ep-builder-main">

        {/* ── Operations strip ─────────────────────────────────────────────── */}
        <div className="ep-ops-strip">
          {lines.map((ln, i) => {
            const lt = calcLineTotals(ln, parts);
            return (
              <button
                key={i}
                type="button"
                className={`ep-op-tab${i === lineIdx ? ' ep-op-tab--active' : ''}`}
                onClick={() => onSelectLine(i)}
              >
                <span className="ep-op-tab-name">{i + 1}. {ln.title || ln.complaint?.slice(0, 24) || 'Operation'}</span>
                <span className="ep-op-tab-total">{fmtMoney(lt.total)}</span>
              </button>
            );
          })}
          {canEdit && (
            <button type="button" className="ep-op-add" onClick={onAddOperation} title="Add another repair operation">
              + Operation
            </button>
          )}
        </div>

        {/* ── Active operation ─────────────────────────────────────────────── */}
        <section className="ep-section">
          <div className="ep-section-header">
            <h3 className="ep-section-title">Operation {lineIdx + 1}</h3>
            <div className="ep-op-controls">
              <label className="ep-tax-exempt" title="Exclude this operation from sales tax (e.g. state inspection)">
                <input
                  type="checkbox"
                  checked={!!line0.taxExempt}
                  onChange={e => canEdit && onUpdateLine('taxExempt', e.target.checked)}
                  disabled={!canEdit}
                />
                Tax exempt
              </label>
              {canEdit && lines.length > 1 && (
                <button type="button" className="ep-op-remove" onClick={() => onRemoveOperation(lineIdx)}>
                  Remove op
                </button>
              )}
            </div>
          </div>
          <input
            className="ep-input ep-input--full ep-op-title"
            placeholder="Operation name — e.g. Air Leak - BR015, PM Service, State Inspection"
            value={line0.title || ''}
            onChange={e => onUpdateLine('title', e.target.value)}
            disabled={!canEdit}
          />
          <div className="ep-three-cs">

            <div className="ep-c-block">
              <div className="ep-c-label-row">
                <label className="ep-c-label">COMPLAINT <span className="ep-c-hint">Customer's description</span></label>
              </div>
              <textarea className="ep-c-textarea" rows={3}
                value={line0.complaint || ''}
                onChange={e => onUpdateLine('complaint', e.target.value)}
                disabled={!canEdit}
                placeholder="Enter customer complaint…" />
            </div>

            <div className="ep-c-block">
              <div className="ep-c-label-row">
                <label className="ep-c-label">CAUSE <span className="ep-c-hint">Tech's diagnosis</span></label>
                {findings.length > 0 && (
                  <button type="button" className="ep-pull-btn"
                    onClick={() => setFindingOpen(o => !o)}>
                    Pull from finding ▾
                  </button>
                )}
              </div>
              {findingOpen && (
                <div className="ep-activity-picker">
                  {findings.map(f => (
                    <button key={f.id} type="button" className="ep-activity-opt"
                      onClick={() => { onUpdateLine('cause', f.text); setFindingOpen(false); }}>
                      {f.text?.slice(0, 120)}
                    </button>
                  ))}
                </div>
              )}
              <textarea className="ep-c-textarea" rows={3}
                value={line0.cause || ''}
                onChange={e => onUpdateLine('cause', e.target.value)}
                disabled={!canEdit}
                placeholder="Enter cause / diagnosis…" />
            </div>

            <div className="ep-c-block">
              <div className="ep-c-label-row">
                <label className="ep-c-label">CORRECTION <span className="ep-c-hint">Proposed repair</span></label>
                {repairs.length > 0 && (
                  <button type="button" className="ep-pull-btn"
                    onClick={() => setRepairOpen(o => !o)}>
                    Pull from repair ▾
                  </button>
                )}
              </div>
              {repairOpen && (
                <div className="ep-activity-picker">
                  {repairs.map(r => (
                    <button key={r.id} type="button" className="ep-activity-opt"
                      onClick={() => { onUpdateLine('correction', r.text); setRepairOpen(false); }}>
                      {r.text?.slice(0, 120)}
                    </button>
                  ))}
                </div>
              )}
              <textarea className="ep-c-textarea" rows={3}
                value={line0.correction || ''}
                onChange={e => onUpdateLine('correction', e.target.value)}
                disabled={!canEdit}
                placeholder="Enter correction / proposed repair…" />
            </div>

          </div>
        </section>

        {/* ── Labor ──────────────────────────────────────────────────────── */}
        <section className="ep-section">
          <div className="ep-section-header">
            <h3 className="ep-section-title">Labor</h3>
            {canEdit && !addLaborOpen && (
              <button className="ep-btn ep-btn--add" onClick={() => {
                setEditLaborId(null);
                setLaborForm({ ...EMPTY_LABOR, rate: String(defaultLaborRate) });
                setAddLaborOpen(true);
              }}>+ Add Labor</button>
            )}
          </div>

          {laborLines.length === 0 && !addLaborOpen && (
            <div className="ep-empty">No labor lines added yet.</div>
          )}

          {laborLines.length > 0 && (
            <div className="ep-labor-table-wrap">
              <table className="ep-labor-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="ep-th-r">Hrs</th>
                    <th className="ep-th-r">Rate</th>
                    <th className="ep-th-r">Amount</th>
                    {canEdit && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {laborLines.map(l => (
                    editLaborId === l.id ? (
                      <tr key={l.id} className="ep-labor-row ep-labor-row--editing">
                        <td colSpan={canEdit ? 5 : 4}>
                          <LaborForm
                            form={laborForm}
                            setForm={setLaborForm}
                            onSubmit={onAddLabor}
                            onCancel={() => { setAddLaborOpen(false); setEditLaborId(null); }}
                            defaultRate={defaultLaborRate}
                            catalogItems={catalogItems}
                            isEdit
                          />
                        </td>
                      </tr>
                    ) : (
                      <tr key={l.id} className="ep-labor-row">
                        <td>
                          <div className="ep-labor-desc">{l.description}</div>
                          {l.techName && <div className="ep-labor-tech">Tech: {l.techName}</div>}
                        </td>
                        <td className="ep-td-r">{l.hours != null ? l.hours : <span className="ep-muted">flat</span>}</td>
                        <td className="ep-td-r">{l.rate != null ? fmtMoney(l.rate) : <span className="ep-muted">—</span>}</td>
                        <td className={`ep-td-r ep-td-amount${(parseFloat(l.amount) || 0) < 0 ? ' ep-td-amount--credit' : ''}`}>
                          {fmtMoney(l.amount)}
                        </td>
                        {canEdit && (
                          <td className="ep-td-actions">
                            <button className="ep-row-btn ep-row-btn--edit" onClick={() => onEditLabor(l)}>✎</button>
                            <button className="ep-row-btn ep-row-btn--del"  onClick={() => onDeleteLabor(l.id)}>×</button>
                          </td>
                        )}
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {addLaborOpen && !editLaborId && (
            <LaborForm
              form={laborForm}
              setForm={setLaborForm}
              onSubmit={onAddLabor}
              onCancel={() => setAddLaborOpen(false)}
              defaultRate={defaultLaborRate}
              catalogItems={catalogItems}
              isEdit={false}
            />
          )}
        </section>

        {/* ── Parts selection (a part belongs to exactly one operation) ───── */}
        {parts.length > 0 && (
          <section className="ep-section">
            <h3 className="ep-section-title">Parts</h3>
            <div className="ep-parts-list">
              {parts.map(p => {
                const ownerIdx  = lines.findIndex(ln => (ln.partLineIds || []).includes(p.id));
                const checked   = ownerIdx === lineIdx;
                const elsewhere = ownerIdx !== -1 && ownerIdx !== lineIdx;
                const lineTotal = (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1);
                return (
                  <label key={p.id} className={`ep-part-row${checked ? ' ep-part-row--checked' : ''}`}>
                    <input type="checkbox" checked={checked}
                      onChange={() => canEdit && onTogglePart(p.id)}
                      disabled={!canEdit} />
                    <div className="ep-part-info">
                      <span className="ep-part-desc">{p.description}</span>
                      {p.partNumber && <span className="ep-part-num">#{p.partNumber}</span>}
                      {p.vendor     && <span className="ep-part-vendor">{p.vendor}</span>}
                      {elsewhere && <span className="ep-part-owner">Op {ownerIdx + 1}</span>}
                    </div>
                    <div className="ep-part-right">
                      <span className="ep-part-qty">×{p.quantity}</span>
                      <span className="ep-part-price">{fmtMoney(lineTotal)}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </section>
        )}

        {/* ── Fees & Tax ──────────────────────────────────────────────────── */}
        <section className="ep-section">
          <h3 className="ep-section-title">Fees &amp; Tax</h3>
          <div className="ep-fees-grid">
            <label className="ep-input-group">
              <span>Shop Supplies</span>
              <div className="ep-fees-row">
                <input className="ep-input ep-input--money" type="number" min="0" step="0.01"
                  placeholder="$0.00"
                  disabled={!!draft.shopSuppliesPct}
                  value={draft.shopSuppliesPct ? '' : (draft.shopSupplies || '')}
                  onChange={e => onUpdateDraft('shopSupplies', parseFloat(e.target.value) || 0)} />
                <span className="ep-fees-or">or</span>
                <input className="ep-input ep-input--pct" type="number" min="0" max="100" step="0.1"
                  placeholder="% of labor"
                  value={draft.shopSuppliesPct || ''}
                  onChange={e => onUpdateDraft('shopSuppliesPct', parseFloat(e.target.value) || null)} />
                <span className="ep-fees-pct">%</span>
              </div>
            </label>
            <label className="ep-input-group">
              <span>Tax Rate</span>
              <div className="ep-fees-row">
                <input className="ep-input ep-input--pct" type="number" min="0" max="100" step="0.01"
                  placeholder="0"
                  value={draft.taxRate || ''}
                  onChange={e => onUpdateDraft('taxRate', parseFloat(e.target.value) || 0)} />
                <span className="ep-fees-pct">%</span>
              </div>
            </label>
          </div>
        </section>

        {/* ── Notes ───────────────────────────────────────────────────────── */}
        <section className="ep-section">
          <h3 className="ep-section-title">Notes</h3>
          <div className="ep-notes-grid">
            <label className="ep-input-group">
              <span>Customer-Facing Notes</span>
              <textarea className="ep-textarea" rows={3}
                placeholder="Shown on estimate document…"
                value={draft.customerNotes || ''}
                onChange={e => onUpdateDraft('customerNotes', e.target.value)}
                disabled={!canEdit} />
            </label>
            <label className="ep-input-group">
              <span>Internal Notes</span>
              <textarea className="ep-textarea" rows={3}
                placeholder="Internal only, not shown to customer…"
                value={draft.notes || ''}
                onChange={e => onUpdateDraft('notes', e.target.value)}
                disabled={!canEdit} />
            </label>
          </div>
        </section>

      </div>{/* end ep-builder-main */}

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside className="ep-sidebar">

        <div className="ep-totals-card">
          <div className="ep-totals-title">{['invoiced', 'paid'].includes(draft.status) ? 'Invoice Total' : 'Estimate Total'}</div>
          <div className="ep-totals-rows">
            <div className="ep-tot-row"><span>Labor</span><span>{fmtMoney(totals.laborSubtotal)}</span></div>
            {totals.partsSubtotal > 0 && (
              <div className="ep-tot-row"><span>Parts</span><span>{fmtMoney(totals.partsSubtotal)}</span></div>
            )}
            {totals.shopSupplies > 0 && (
              <div className="ep-tot-row"><span>Shop Supplies</span><span>{fmtMoney(totals.shopSupplies)}</span></div>
            )}
            <div className="ep-tot-row ep-tot-row--sub"><span>Subtotal</span><span>{fmtMoney(totals.subtotal)}</span></div>
            {totals.tax > 0 && (
              <div className="ep-tot-row"><span>Tax ({draft.taxRate}%)</span><span>{fmtMoney(totals.tax)}</span></div>
            )}
          </div>
          <div className="ep-totals-total">
            <span>TOTAL</span><span>{fmtMoney(totals.total)}</span>
          </div>
          {paidAmt > 0 && (
            <div className="ep-totals-rows ep-paid-block">
              <div className="ep-tot-row ep-paid-row"><span>Paid to date</span><span>−{fmtMoney(paidAmt)}</span></div>
              <div className="ep-tot-row ep-balance-row"><span>BALANCE DUE</span><span>{fmtMoney(remainingAmt)}</span></div>
            </div>
          )}
        </div>

        <div className="ep-status-card">
          <div className="ep-status-row">
            <span className="ep-status-badge-lg" style={{ color: sc.color, background: sc.bg }}>
              {sc.label}
            </span>
            {draft.version > 1 && <span className="ep-version-tag">v{draft.version}</span>}
          </div>

          {draft.approvedAt && (
            <div className="ep-approval-note">
              ✓ Approved{draft.approvedBy ? ` by ${draft.approvedBy}` : ''}{draft.approvalMethod ? ` via ${draft.approvalMethod.replace('_', ' ')}` : ''}
            </div>
          )}
          {draft.declinedAt && (
            <div className="ep-decline-note">✕ Declined{draft.declineReason ? `: ${draft.declineReason}` : ''}</div>
          )}

          {canAct && (
            <div className="ep-action-stack">
              {canEdit && (
                <button className="ep-btn ep-btn--save" onClick={onSave} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save Draft'}
                </button>
              )}

              {canEdit && !draft.lockedAt && !discountMode && (
                <button className="ep-btn ep-btn--ghost" onClick={() => setDiscountMode(true)}>
                  Add Discount
                </button>
              )}

              {discountMode && (
                <div className="ep-mini-form">
                  <input className="ep-input ep-input--full" placeholder="Label (e.g. Fleet discount)"
                    value={discountForm.description}
                    onChange={e => setDiscountForm(f => ({ ...f, description: e.target.value }))} />
                  <div className="ep-discount-row">
                    <span className="ep-discount-minus">−</span>
                    <input className="ep-input ep-input--full" type="number" min="0" step="0.01"
                      autoFocus
                      placeholder={discountForm.mode === 'pct' ? 'Percent off' : 'Amount off'}
                      value={discountForm.value}
                      onChange={e => setDiscountForm(f => ({ ...f, value: e.target.value }))} />
                    <div className="ep-discount-modes">
                      <button type="button"
                        className={`ep-discount-mode${discountForm.mode !== 'pct' ? ' ep-discount-mode--on' : ''}`}
                        onClick={() => setDiscountForm(f => ({ ...f, mode: 'amt' }))}>$</button>
                      <button type="button"
                        className={`ep-discount-mode${discountForm.mode === 'pct' ? ' ep-discount-mode--on' : ''}`}
                        onClick={() => setDiscountForm(f => ({ ...f, mode: 'pct' }))}>%</button>
                    </div>
                  </div>
                  {discountForm.mode === 'pct' && (
                    <span className="ep-discount-hint">
                      {fmtMoney(((parseFloat(discountForm.value) || 0) / 100) * (totals.laborSubtotal + totals.partsSubtotal))}
                      {' '}off labor + parts
                    </span>
                  )}
                  <div className="ep-mini-form-btns">
                    <button className="ep-btn ep-btn--approve ep-btn--sm" onClick={onSaveDiscount}>Add</button>
                    <button className="ep-btn ep-btn--ghost ep-btn--sm" onClick={() => setDiscountMode(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {!approvalMode && !declineMode && ['draft', 'sent'].includes(draft.status) && (
                <>
                  {draft.status === 'draft' && (
                    <button className="ep-btn ep-btn--send" onClick={onRequestApproval}>Request Approval →</button>
                  )}
                  {draft.status === 'sent' && (
                    <button className="ep-btn ep-btn--send" onClick={onRequestApproval}>Resend Approval Link</button>
                  )}
                  <button className="ep-btn ep-btn--approve" onClick={() => setApprovalMode(true)}>
                    ✓ Mark Approved
                  </button>
                  <button className="ep-btn ep-btn--decline" onClick={() => setDeclineMode(true)}>
                    ✕ Mark Declined
                  </button>
                  <button className="ep-btn ep-btn--primary" onClick={onCreateInvoice}
                    title="Post charges straight to an invoice — no estimate approval step">
                    Post Charges — Invoice →
                  </button>
                </>
              )}

              {approvalMode && (
                <div className="ep-mini-form">
                  <select className="ep-input ep-input--full" value={approvalForm.method}
                    onChange={e => setApprovalForm(f => ({ ...f, method: e.target.value }))}>
                    {APPROVAL_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input className="ep-input ep-input--full" placeholder="Approved by (name)"
                    value={approvalForm.approvedBy}
                    onChange={e => setApprovalForm(f => ({ ...f, approvedBy: e.target.value }))} />
                  <input className="ep-input ep-input--full" placeholder="Notes (optional)"
                    value={approvalForm.notes}
                    onChange={e => setApprovalForm(f => ({ ...f, notes: e.target.value }))} />
                  <div className="ep-mini-form-btns">
                    <button className="ep-btn ep-btn--approve ep-btn--sm" onClick={onApprove}>Confirm</button>
                    <button className="ep-btn ep-btn--ghost ep-btn--sm" onClick={() => setApprovalMode(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {declineMode && (
                <div className="ep-mini-form">
                  <input className="ep-input ep-input--full" placeholder="Reason for decline"
                    value={declineReason}
                    onChange={e => setDeclineReason(e.target.value)} />
                  <div className="ep-mini-form-btns">
                    <button className="ep-btn ep-btn--decline ep-btn--sm" onClick={onDecline}>Confirm</button>
                    <button className="ep-btn ep-btn--ghost ep-btn--sm" onClick={() => setDeclineMode(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {sentLink && (
                <div className="ep-share-link">
                  <strong>Estimate sent — awaiting approval</strong>
                  <input className="ep-input ep-input--full" value={sentLink} readOnly onFocus={e => e.target.select()} />
                </div>
              )}

              {draft.status === 'approved' && (
                <button className="ep-btn ep-btn--primary" onClick={onCreateInvoice}>
                  Create Invoice →
                </button>
              )}

              {draft.status === 'invoiced' && !draft.lockedAt && (
                <button className="ep-btn ep-btn--primary" onClick={onFinalizeInvoice}>
                  Finalize Invoice
                </button>
              )}

              {draft.status === 'invoiced' && !paymentMode && remainingAmt > 0 && (
                <button className="ep-btn ep-btn--approve"
                  onClick={() => {
                    setPaymentForm(f => ({ ...f, amount: remainingAmt.toFixed(2) }));
                    setPaymentMode(true);
                  }}>
                  $ Record Payment
                </button>
              )}

              {paymentMode && (
                <div className="ep-mini-form">
                  <input className="ep-input ep-input--full" type="number" min="0" step="0.01"
                    placeholder={`Amount (balance ${fmtMoney(remainingAmt)})`}
                    value={paymentForm.amount}
                    onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} />
                  <select className="ep-input ep-input--full" value={paymentForm.method}
                    onChange={e => setPaymentForm(f => ({ ...f, method: e.target.value }))}>
                    {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                  <input className="ep-input ep-input--full" placeholder="Reference / check # (optional)"
                    value={paymentForm.reference}
                    onChange={e => setPaymentForm(f => ({ ...f, reference: e.target.value }))} />
                  <div className="ep-mini-form-btns">
                    <button className="ep-btn ep-btn--approve ep-btn--sm" onClick={onRecordPayment}>Record</button>
                    <button className="ep-btn ep-btn--ghost ep-btn--sm" onClick={() => setPaymentMode(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {(draft.payments || []).length > 0 && (
                <div className="ep-payments">
                  <span className="ep-payments-title">Payments</span>
                  {draft.payments.map(p => (
                    <div key={p.id} className="ep-payment-row">
                      <span>{fmtDateShort(p.receivedAt)}</span>
                      <span className="ep-payment-method">
                        {(PAYMENT_METHODS.find(m => m.value === p.method)?.label || p.method || '—')}{p.reference ? ` #${p.reference}` : ''}
                      </span>
                      <span className="ep-payment-amt">{fmtMoney(p.amount)}</span>
                    </div>
                  ))}
                </div>
              )}

              {['invoiced', 'paid'].includes(draft.status) && (
                <>
                  <button className="ep-btn ep-btn--ghost" onClick={() => window.print()}>Print Invoice</button>
                  <button className="ep-btn ep-btn--ghost" onClick={onEmailInvoice}>Email Invoice</button>
                  {draft.lockedAt && (
                    <button className="ep-btn ep-btn--ghost" onClick={onExportQbo}>Export to QuickBooks</button>
                  )}
                  {draft.status === 'invoiced' && (draft.payments || []).length === 0 && (
                    <button className="ep-btn ep-btn--ghost" onClick={onRevertInvoice} title="Made a mistake? Back to an editable estimate — nothing is deleted">
                      ↩ Undo Invoice
                    </button>
                  )}
                </>
              )}

              {exportOpen && (
                <div className="ep-mini-form ep-export-box">
                  <strong>Export this invoice to QuickBooks</strong>
                  {Object.values(QB_FORMATS).map(fmt => (
                    <div key={fmt.id} className="ep-export-fmt">
                      <button className="ep-btn ep-btn--primary ep-btn--sm" onClick={() => onDownloadQbo(fmt.id)}>
                        {fmt.label}
                      </button>
                      <span className="ep-export-hint">{fmt.hint}</span>
                    </div>
                  ))}
                  {exportNotes.length > 0 && (
                    <ul className="ep-export-notes">
                      {exportNotes.map(n => <li key={n}>{n}</li>)}
                    </ul>
                  )}
                  <div className="ep-mini-form-btns">
                    <button className="ep-btn ep-btn--ghost ep-btn--sm" onClick={onCopyQbo}>Copy CSV to clipboard</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

      </aside>
    </div>
  );
}

// ─── Preview mode ─────────────────────────────────────────────────────────────

function PreviewMode({ draft, parts, totals, truck, settings }) {
  // Customer-facing paper carries the SHOP's name, never the software's
  const shopName      = settings?.business?.shopName || settings?.business?.companyName || '';
  const shopAddress   = remitToLines(settings);
  const logoSrc       = settings?.branding?.logoUrl || settings?.branding?.logoBase64 || null;
  const sc            = STATUS_CONFIG[draft.status] || STATUS_CONFIG.draft;
  const invoiceMode   = ['invoiced', 'paid'].includes(draft.status);
  const qboDates      = getQBODates(draft, settings);
  const invoiceNo     = getQBOInvoiceNumber(draft, truck);

  return (
    <div className="ep-preview">
      <div className="ep-doc">

        {/* Header */}
        <div className="ep-doc-header">
          <div className="ep-doc-shop">
            {logoSrc && <img className="ep-doc-logo" src={logoSrc} alt="" />}
            <div className="ep-doc-shop-name">{shopName}</div>
            {shopAddress.map(l => <div key={l} className="ep-doc-shop-line">{l}</div>)}
            <div className="ep-doc-doc-label">{invoiceMode ? 'INVOICE' : 'ESTIMATE'}</div>
          </div>
          <div className="ep-doc-meta">
            <div className="ep-doc-meta-row"><span>RO #</span><strong>{truck.repairOrder}</strong></div>
            {/* The number their accounts payable matches against. Printed on
                the estimate too, so a fleet can approve against their own PO. */}
            {truck.poNumber && <div className="ep-doc-meta-row"><span>PO #</span><strong>{truck.poNumber}</strong></div>}
            {invoiceMode && <div className="ep-doc-meta-row"><span>Invoice #</span><strong>{invoiceNo}</strong></div>}
            <div className="ep-doc-meta-row"><span>{invoiceMode ? 'Invoice Date' : 'Date'}</span><strong>{invoiceMode ? qboDates.invoiceDate : fmtDateLong()}</strong></div>
            {invoiceMode && <div className="ep-doc-meta-row"><span>Due Date</span><strong>{qboDates.dueDate}</strong></div>}
            <div className="ep-doc-meta-row"><span>Version</span><strong>v{draft.version}</strong></div>
            <div className="ep-doc-meta-row">
              <span>Status</span>
              <strong style={{ color: sc.color }}>{sc.label}</strong>
            </div>
          </div>
        </div>

        {/* Customer + Unit */}
        <div className="ep-doc-parties">
          <div className="ep-doc-party">
            <div className="ep-doc-party-label">CUSTOMER</div>
            <div className="ep-doc-party-name">{truck.companyName}</div>
            {truck.contactPerson && <div className="ep-doc-party-line">{truck.contactPerson}</div>}
            {truck.phone         && <div className="ep-doc-party-line">{truck.phone}</div>}
            {truck.email         && <div className="ep-doc-party-line">{truck.email}</div>}
          </div>
          <div className="ep-doc-party">
            <div className="ep-doc-party-label">UNIT</div>
            <div className="ep-doc-party-name">
              {[truck.year, truck.make, truck.model].filter(Boolean).join(' ') || '—'}
            </div>
            {truck.truckNumber && <div className="ep-doc-party-line">Unit # {truck.truckNumber}</div>}
            {truck.vin         && <div className="ep-doc-party-line">VIN: {truck.vin}</div>}
            {truck.mileage     && <div className="ep-doc-party-line">{truck.mileage} mi</div>}
          </div>
        </div>

        <hr className="ep-doc-rule" />

        {/* Operations — each with its own story, labor, parts, subtotal */}
        {(draft.lines || []).map((ln, i) => {
          const opLabor = ln.laborLines || [];
          const opParts = (parts || []).filter(p => (ln.partLineIds || []).includes(p.id));
          const lt = calcLineTotals(ln, parts);
          if (!ln.title && !ln.complaint && opLabor.length === 0 && opParts.length === 0) return null;
          const multiOp = (draft.lines || []).length > 1;
          return (
            <div key={i} className="ep-doc-op">
              {multiOp && (
                <div className="ep-doc-op-head">
                  <span className="ep-doc-op-name">OPERATION {i + 1}{ln.title ? ` — ${ln.title}` : ''}</span>
                  <span className="ep-doc-op-right">
                    {ln.taxExempt && <em className="ep-doc-op-exempt">TAX EXEMPT</em>}
                    <strong>{fmtMoney(lt.total)}</strong>
                  </span>
                </div>
              )}
              {!multiOp && ln.title && (
                <div className="ep-doc-op-head">
                  <span className="ep-doc-op-name">{ln.title}</span>
                  {ln.taxExempt && <em className="ep-doc-op-exempt">TAX EXEMPT</em>}
                </div>
              )}

              {(ln.complaint || ln.cause || ln.correction) && (
                <div className="ep-doc-3cs">
                  {ln.complaint && (
                    <div className="ep-doc-c">
                      <div className="ep-doc-c-label">COMPLAINT</div>
                      <div className="ep-doc-c-text">{ln.complaint}</div>
                    </div>
                  )}
                  {ln.cause && (
                    <div className="ep-doc-c">
                      <div className="ep-doc-c-label">CAUSE</div>
                      <div className="ep-doc-c-text">{ln.cause}</div>
                    </div>
                  )}
                  {ln.correction && (
                    <div className="ep-doc-c">
                      <div className="ep-doc-c-label">CORRECTION</div>
                      <div className="ep-doc-c-text">{ln.correction}</div>
                    </div>
                  )}
                </div>
              )}

              {opLabor.length > 0 && (
                <div className="ep-doc-table-section">
                  <div className="ep-doc-table-title">Labor</div>
                  <table className="ep-doc-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="ep-td-r">Hours</th>
                        <th className="ep-td-r">Rate</th>
                        <th className="ep-td-r">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opLabor.map(l => (
                        <tr key={l.id}>
                          <td>
                            {l.description}
                            {l.techName && <span className="ep-doc-tech"> — {l.techName}</span>}
                          </td>
                          <td className="ep-td-r">{l.hours != null ? l.hours : '—'}</td>
                          <td className="ep-td-r">{l.rate != null ? fmtMoney(l.rate) : '—'}</td>
                          <td className="ep-td-r">{fmtMoney(l.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {opParts.length > 0 && (
                <div className="ep-doc-table-section">
                  <div className="ep-doc-table-title">Parts</div>
                  <table className="ep-doc-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Part #</th>
                        <th className="ep-td-r">Qty</th>
                        <th className="ep-td-r">Price</th>
                        <th className="ep-td-r">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {opParts.map(p => {
                        const amt = (parseFloat(p.price) || 0) * (parseFloat(p.quantity) || 1);
                        return (
                          <tr key={p.id}>
                            <td>{p.description}</td>
                            <td className="ep-doc-muted">{p.partNumber || '—'}</td>
                            <td className="ep-td-r">{p.quantity}</td>
                            <td className="ep-td-r">{fmtMoney(p.price)}</td>
                            <td className="ep-td-r">{fmtMoney(amt)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}

        {/* Totals */}
        <div className="ep-doc-totals">
          <div className="ep-doc-totals-inner">
            <div className="ep-doc-tot-row"><span>Labor</span><span>{fmtMoney(totals.laborSubtotal)}</span></div>
            {totals.partsSubtotal > 0 && (
              <div className="ep-doc-tot-row"><span>Parts</span><span>{fmtMoney(totals.partsSubtotal)}</span></div>
            )}
            {totals.shopSupplies > 0 && (
              <div className="ep-doc-tot-row"><span>Shop Supplies</span><span>{fmtMoney(totals.shopSupplies)}</span></div>
            )}
            <div className="ep-doc-tot-row ep-doc-tot-row--sub">
              <span>Subtotal</span><span>{fmtMoney(totals.subtotal)}</span>
            </div>
            {totals.tax > 0 && (
              <div className="ep-doc-tot-row">
                <span>Tax ({draft.taxRate}%)</span><span>{fmtMoney(totals.tax)}</span>
              </div>
            )}
            <div className="ep-doc-tot-row ep-doc-tot-row--total">
              <span>TOTAL</span><span>{fmtMoney(totals.total)}</span>
            </div>
            {(draft.payments || []).length > 0 && (
              <>
                <div className="ep-doc-tot-row">
                  <span>Paid to date</span>
                  <span>−{fmtMoney((draft.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0))}</span>
                </div>
                <div className="ep-doc-tot-row ep-doc-tot-row--total">
                  <span>BALANCE DUE</span>
                  <span>{fmtMoney(Math.max(0, totals.total - (draft.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0)))}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Customer notes */}
        {draft.customerNotes && (
          <div className="ep-doc-notes">
            <div className="ep-doc-notes-label">NOTES</div>
            <div className="ep-doc-notes-text">{draft.customerNotes}</div>
          </div>
        )}

        {invoiceMode && (
          <div className="ep-doc-payment-terms">
            Net {qboDates.paymentTermsDays} — Due {qboDates.dueDate}
          </div>
        )}

        {/* Approval / signature */}
        {invoiceMode ? (
          <div className="ep-doc-thanks">Thank you for your business.</div>
        ) : draft.approvedAt ? (
          <div className="ep-doc-approved">
            ✓ Approved{draft.approvedBy ? ` by ${draft.approvedBy}` : ''}{' '}
            {draft.approvalMethod ? `· ${draft.approvalMethod.replace('_', ' ')}` : ''}{' '}
            · {fmtDateShort(draft.approvedAt)}
          </div>
        ) : draft.declinedAt ? (
          <div className="ep-doc-declined">
            ✕ Declined · {fmtDateShort(draft.declinedAt)}{draft.declineReason ? ` — ${draft.declineReason}` : ''}
          </div>
        ) : (
          <div className="ep-doc-sig-block">
            <div className="ep-doc-sig-line">
              <div className="ep-doc-sig-field"><span>Customer Signature</span></div>
              <div className="ep-doc-sig-field ep-doc-sig-field--sm"><span>Date</span></div>
            </div>
            <div className="ep-doc-sig-note">
              By signing, customer authorizes the described repairs at the stated price.
            </div>
          </div>
        )}

        {/* Disclaimer — edit under Settings → Billing */}
        {settings?.pricing?.invoiceDisclaimer && (
          <div className="ep-doc-disclaimer">{settings.pricing.invoiceDisclaimer}</div>
        )}

      </div>

      <div className="ep-preview-toolbar">
        <button className="ep-btn ep-btn--primary" onClick={() => window.print()}>
          {invoiceMode ? 'Print Invoice' : '⎙ Print Estimate'}
        </button>
      </div>
    </div>
  );
}

// ─── EstimatePanel ────────────────────────────────────────────────────────────

export default function EstimatePanel({ truck, onClose, onSave, settings, canEdit, inline = false, initialMode = 'builder', customerTermsDays = null, poRequired = false }) {
  const parts            = truck.parts || [];
  const activities       = truck.activities || [];
  const defaultLaborRate = settings?.pricing?.defaultLaborRate ?? 95;
  // This customer's own Net terms when the caller resolved them, the shop
  // default otherwise. Stamped onto the estimate, never re-read afterwards.
  const termsDays        = customerTermsDays ?? settings?.pricing?.paymentTermsDays ?? 30;
  const defaultTaxRate   = settings?.pricing?.taxRate ?? 0;

  const findings = activities.filter(a => a.type === 'finding' && a.text);
  const repairs  = activities.filter(a => a.type === 'repair'  && a.text);

  function makeNew() {
    return {
      id: makeId('est'), truckId: truck.id, roNumber: truck.repairOrder,
      status: 'draft',
      lines: [{
        title:       '',
        complaint:   truck.reason || '',
        cause:       findings[0]?.text || '',
        correction:  repairs[0]?.text  || '',
        laborLines:  [],
        // CDK-style: charges accumulate on the RO as work happens — every
        // part already on the RO starts included (uncheck to exclude)
        partLineIds: (truck.parts || []).map(p => p.id),
        taxExempt:   false,
      }],
      shopSupplies: 0, shopSuppliesPct: null, taxRate: defaultTaxRate,
      subtotal: 0, tax: 0, total: 0,
      notes: '', customerNotes: '', token: null,
      sentAt: null, approvedAt: null, approvalMethod: null,
      approvedBy: '', approvalNotes: '', declinedAt: null, declineReason: '',
      invoicedAt: null, lockedAt: null, paymentTermsDays: termsDays,
      createdBy: getCurrentRole(), createdAt: nowSeconds(), updatedAt: nowSeconds(), version: 1,
    };
  }

  const existing = (truck.estimates || [])[0] || null;
  const [draft, setDraft]     = useState(() => existing ? JSON.parse(JSON.stringify(clean(existing))) : makeNew());
  // 'builder' = the estimate editor; 'preview' = the invoice/estimate document.
  // Callers open straight to the invoice via initialMode='preview'.
  const [mode, setMode]       = useState(initialMode);
  const [saving, setSaving]   = useState(false);
  const [savedTs, setSavedTs] = useState(null);

  const [addLaborOpen, setAddLaborOpen] = useState(false);
  const [laborForm,    setLaborForm]    = useState({ ...EMPTY_LABOR, rate: String(defaultLaborRate) });
  const [editLaborId,  setEditLaborId]  = useState(null);

  const [findingOpen, setFindingOpen] = useState(false);
  const [repairOpen,  setRepairOpen]  = useState(false);

  const [approvalMode, setApprovalMode] = useState(false);
  const [approvalForm, setApprovalForm] = useState({ method: 'phone', approvedBy: '', notes: '' });
  const [declineMode,  setDeclineMode]  = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [sentLink, setSentLink] = useState('');
  const [exportOpen, setExportOpen] = useState(false);
  const [paymentMode, setPaymentMode] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'check', reference: '' });
  const [discountMode, setDiscountMode] = useState(false);
  const [discountForm, setDiscountForm] = useState({ description: '', value: '', mode: 'amt' });

  // Menu-priced jobs from Settings -> Service Catalog. Inactive and unnamed
  // rows never reach the estimate builder.
  const catalogItems = (settings?.catalog?.items || [])
    .filter(i => i && i.name && i.active !== false);

  // ── Operations (one EstimateLine per repair operation) ─────────────────────
  const [activeLineIdx, setActiveLineIdx] = useState(0);
  const lines = draft.lines?.length ? draft.lines : [emptyEstimateLine()];
  const lineIdx = Math.min(activeLineIdx, lines.length - 1);
  const line0 = lines[lineIdx] || {};
  const canModify = canEdit && !draft.lockedAt;

  function updateLine(field, value) {
    setDraft(d => {
      const current = d.lines?.length ? d.lines : [emptyEstimateLine()];
      const next = current.map((ln, i) => (i === lineIdx ? { ...ln, [field]: value } : ln));
      return { ...d, lines: next };
    });
  }

  function handleAddOperation() {
    setDraft(d => {
      const current = d.lines?.length ? d.lines : [emptyEstimateLine()];
      return { ...d, lines: [...current, emptyEstimateLine()] };
    });
    setActiveLineIdx(lines.length); // the new one
    setAddLaborOpen(false);
    setEditLaborId(null);
  }

  function handleRemoveOperation(idx) {
    const target = lines[idx];
    const hasContent = target && (target.laborLines?.length || target.partLineIds?.length || target.complaint || target.title);
    if (hasContent && !window.confirm(`Remove operation ${idx + 1}${target.title ? ` (${target.title})` : ''}? Its labor lines come off the ticket.`)) return;
    setDraft(d => {
      const current = d.lines?.length ? d.lines : [emptyEstimateLine()];
      const next = current.filter((_, i) => i !== idx);
      return { ...d, lines: next.length ? next : [emptyEstimateLine()] };
    });
    setActiveLineIdx(i => Math.max(0, i >= idx ? i - 1 : i));
  }

  function updateDraft(field, value) {
    setDraft(d => ({ ...d, [field]: value }));
  }

  // ── Labor ──────────────────────────────────────────────────────────────────

  function handleAddLabor(e) {
    e.preventDefault();
    if (!laborForm.description.trim()) return;
    const amount  = calcLaborAmount(laborForm.hours, laborForm.rate, laborForm.flatPrice, laborForm.useFlatRate);
    const newLine = {
      id:           editLaborId || makeId('lab'),
      description:  laborForm.description.trim(),
      catalogItemId: laborForm.catalogItemId || null,
      hours:        !laborForm.useFlatRate && laborForm.hours      ? parseFloat(laborForm.hours)     : null,
      rate:         !laborForm.useFlatRate && laborForm.rate       ? parseFloat(laborForm.rate)      : null,
      flatPrice:    laborForm.useFlatRate  && laborForm.flatPrice  ? parseFloat(laborForm.flatPrice) : null,
      amount,
      techName: laborForm.techName || null,
      notes:    laborForm.notes    || null,
    };
    const current = line0.laborLines || [];
    if (editLaborId) {
      updateLine('laborLines', current.map(l => l.id === editLaborId ? newLine : l));
    } else {
      updateLine('laborLines', [...current, newLine]);
    }
    setLaborForm({ ...EMPTY_LABOR, rate: String(defaultLaborRate) });
    setAddLaborOpen(false);
    setEditLaborId(null);
  }

  function handleDeleteLabor(id) {
    updateLine('laborLines', (line0.laborLines || []).filter(l => l.id !== id));
  }

  function handleEditLabor(l) {
    // No hours means the line was priced flat — including discounts, and
    // including older discount lines that stored only a negative `amount`.
    // Reopening those as hourly would silently recompute them to $0.
    const isFlat = l.hours == null;
    setLaborForm({
      description: l.description,
      hours:       l.hours     != null ? String(l.hours)     : '',
      rate:        l.rate      != null ? String(l.rate)      : String(defaultLaborRate),
      flatPrice:   l.flatPrice != null ? String(l.flatPrice)
                   : (isFlat ? String(parseFloat(l.amount) || 0) : ''),
      techName:    l.techName  || '',
      notes:       l.notes     || '',
      useFlatRate: isFlat,
      catalogItemId: l.catalogItemId || '',
    });
    setEditLaborId(l.id);
    setAddLaborOpen(true);
  }

  function handleTogglePart(partId) {
    if (!canModify) return;
    // A part belongs to one operation — claiming it here releases it elsewhere
    setDraft(d => {
      const current = d.lines?.length ? d.lines : [emptyEstimateLine()];
      const mine = current[lineIdx]?.partLineIds || [];
      const removing = mine.includes(partId);
      const next = current.map((ln, i) => {
        const ids = ln.partLineIds || [];
        if (i === lineIdx) {
          return { ...ln, partLineIds: removing ? ids.filter(id => id !== partId) : [...ids, partId] };
        }
        return ids.includes(partId) ? { ...ln, partLineIds: ids.filter(id => id !== partId) } : ln;
      });
      return { ...d, lines: next };
    });
  }

  // ── Save / status ──────────────────────────────────────────────────────────

  async function doSave(overrideDraft, options = {}) {
    setSaving(true);
    const d      = overrideDraft || draft;
    const totals = calcTotals(d, parts);
    await onSave(clean({ ...d, subtotal: totals.subtotal, tax: totals.tax, total: totals.total, updatedAt: nowSeconds() }), options);
    setSavedTs(Date.now());
    setSaving(false);
  }

  async function handleRequestApproval() {
    const token = draft.token || makePublicToken();
    const url = estimateApprovalUrl(token);
    const u = { ...draft, status: 'sent', sentAt: nowSeconds(), token };
    setDraft(u);
    setSentLink(url);
    await doSave(u);
    try {
      await navigator.clipboard?.writeText(url);
    } catch {}
    const nextTotals = calcTotals(u, parts);
    const subject = `Estimate for ${truck.companyName} - RO ${truck.repairOrder}`;
    const body = plainTextEstimate(u, u.lines?.[0] || {}, parts, nextTotals, truck, settings, url, false);
    const to = truck.email || '';
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  async function handleApprove() {
    const nextTotals = calcTotals(draft, parts);
    const u = {
      ...draft, status: 'approved', approvedAt: nowSeconds(),
      approvalMethod: approvalForm.method, approvedBy: approvalForm.approvedBy, approvalNotes: approvalForm.notes,
    };
    setDraft(u); setApprovalMode(false);
    await doSave(u, {
      activities: [makeActivity('authorization', `Estimate approved - ${approvalForm.method} - ${approvalForm.approvedBy || 'Unknown'} - ${fmtMoney(nextTotals.total)}`, truck)],
    });
  }

  async function handleDecline() {
    const u = { ...draft, status: 'declined', declinedAt: nowSeconds(), declineReason };
    setDraft(u); setDeclineMode(false);
    await doSave(u, {
      activities: [makeActivity('note', `Estimate declined${declineReason ? ` - ${declineReason}` : ''}`, truck)],
    });
  }

  async function handleCreateInvoice() {
    // A warning, not a block. The shop knows its customers, and refusing to
    // invoice would be worse than an invoice that comes back for a PO.
    if (poRequired && !truck.poNumber && !window.confirm(
      `${truck.companyName} requires a PO number on every invoice, and this job has none.\n\nInvoice anyway?`
    )) return;
    const u = {
      ...draft,
      status: 'invoiced',
      invoicedAt: draft.invoicedAt || nowSeconds(),
      paymentTermsDays: draft.paymentTermsDays || termsDays,
    };
    setDraft(u);
    setMode('preview');
    await doSave(u);
  }

  async function handleRecordPayment() {
    const totalsNow = calcTotals(draft, parts);
    const alreadyPaid = (draft.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    const remaining = Math.max(0, totalsNow.total - alreadyPaid);
    const amount = parseFloat(paymentForm.amount);
    const applied = Number.isFinite(amount) && amount > 0 ? Math.min(amount, remaining) : remaining;
    if (applied <= 0) { setPaymentMode(false); return; }

    const methodLabel = PAYMENT_METHODS.find(m => m.value === paymentForm.method)?.label || paymentForm.method;
    const entry = {
      id: makeId('pay'),
      amount: applied,
      method: paymentForm.method,
      reference: paymentForm.reference.trim() || null,
      note: null,
      receivedAt: nowSeconds(),
      recordedBy: getCurrentRole(),
    };
    const payments = [...(draft.payments || []), entry];
    const remainingAfter = Math.max(0, totalsNow.total - alreadyPaid - applied);
    // Fully collected -> paid; anything still owed keeps it an open invoice
    const u = {
      ...draft,
      payments,
      status: remainingAfter <= 0.005 ? 'paid' : 'invoiced',
      updatedAt: nowSeconds(),
    };
    setDraft(u);
    setPaymentMode(false);
    setPaymentForm({ amount: '', method: 'check', reference: '' });
    await doSave(u, {
      activities: [makeActivity('authorization',
        `Payment recorded - ${fmtMoney(applied)} - ${methodLabel}${entry.reference ? ` #${entry.reference}` : ''}${remainingAfter > 0 ? ` - ${fmtMoney(remainingAfter)} remaining` : ' - PAID IN FULL'}`,
        truck)],
    });
  }

  async function handleRevertInvoice() {
    if ((draft.payments || []).length > 0) {
      alert('Payments have been recorded against this invoice — it cannot be reverted. Void the payments with your accountant first.');
      return;
    }
    if (!window.confirm('Undo this invoice? It goes back to being an estimate — nothing is deleted.')) return;
    const u = {
      ...draft,
      // Back to where it was before invoicing: approved if it had approval,
      // otherwise a plain draft (the direct post-charges path)
      status: draft.approvedAt ? 'approved' : 'draft',
      invoicedAt: null,
      lockedAt: null,
      updatedAt: nowSeconds(),
    };
    setDraft(u);
    setMode('builder');
    await doSave(u, {
      activities: [makeActivity('note', `Invoice reverted to ${u.status === 'approved' ? 'approved estimate' : 'draft'}`, truck)],
    });
  }

  async function handleFinalizeInvoice() {
    const u = {
      ...draft,
      status: 'invoiced',
      invoicedAt: draft.invoicedAt || nowSeconds(),
      lockedAt: nowSeconds(),
      paymentTermsDays: draft.paymentTermsDays || termsDays,
    };
    setDraft(u);
    setMode('preview');
    await doSave(u, {
      activities: [makeActivity('authorization', `Invoice finalized - ${getQBOInvoiceNumber(u, truck)} - ${fmtMoney(calcTotals(u, parts).total)}`, truck)],
    });
  }

  // A discount is a negative flat-rate labor line — the shape calcTotals already
  // sums and the QuickBooks export already knows how to net out. flatPrice is
  // set (not just amount) so the line reopens in the labor form as flat and can
  // be edited or deleted like any other line.
  function handleSaveDiscount() {
    const entered = Math.abs(parseFloat(discountForm.value) || 0);
    if (!entered) return;
    const base = totals.laborSubtotal + totals.partsSubtotal;
    const off  = discountForm.mode === 'pct'
      ? Math.round(base * (entered / 100) * 100) / 100
      : entered;
    if (!off) return;
    const label = discountForm.description.trim()
      || (discountForm.mode === 'pct' ? `Discount (${entered}%)` : 'Discount');
    const current = line0.laborLines || [];
    updateLine('laborLines', [
      ...current,
      {
        id: makeId('disc'),
        description: label,
        catalogItemId: null,
        hours: null,
        rate: null,
        flatPrice: -off,
        amount: -off,
        techName: null,
        notes: null,
      },
    ]);
    setDiscountForm({ description: '', value: '', mode: 'amt' });
    setDiscountMode(false);
  }

  function handleEmailInvoice() {
    const invoiceTotals = calcTotals(draft, parts);
    const invoiceNo = getQBOInvoiceNumber(draft, truck);
    const subject = `Invoice ${invoiceNo} - ${truck.companyName} - ${fmtMoney(invoiceTotals.total)}`;
    const body = plainTextEstimate(draft, draft.lines?.[0] || {}, parts, invoiceTotals, truck, settings, null, true);
    const to = truck.email || '';
    window.location.href = `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  }

  // The invoice priced through the shared calcTotals — what every QuickBooks
  // format renders from.
  function pricedInvoice() {
    return { ...draft, ...calcTotals(draft, parts) };
  }

  function handleDownloadQbo(formatId = settings?.accounting?.quickbooksFormat || DEFAULT_QB_FORMAT) {
    downloadQBInvoice(pricedInvoice(), truck, settings, formatId);
  }

  async function handleCopyQbo() {
    const text = buildQBOInvoiceCsv(pricedInvoice(), truck, settings);
    try {
      await navigator.clipboard?.writeText(text);
      setSavedTs(Date.now());
    } catch {
      prompt('Copy QuickBooks CSV:', text);
    }
  }

  const totals = calcTotals(draft, parts);
  const sc     = STATUS_CONFIG[draft.status] || STATUS_CONFIG.draft;

  return (
    <div className={inline ? 'ep-inline-wrap' : 'ep-overlay'}>
      <div className={inline ? 'ep-inline-panel' : 'ep-panel'}>

        <div className="ep-header">
          <div className="ep-header-left">
            <span className="ep-title">Estimate</span>
            <span className="ep-hdr-sep">·</span>
            <span className="ep-hdr-ro">RO {truck.repairOrder}</span>
            {truck.truckNumber && <><span className="ep-hdr-sep">·</span><span className="ep-hdr-unit">Unit {truck.truckNumber}</span></>}
            <span className="ep-hdr-sep">·</span>
            <span className="ep-hdr-customer">{truck.companyName}</span>
            <span className="ep-status-badge ep-status-badge--sm" style={{ color: sc.color, background: sc.bg }}>
              {sc.label}
            </span>
            {draft.version > 1 && <span className="ep-version-tag">v{draft.version}</span>}
          </div>
          <div className="ep-header-right">
            {savedTs && <span className="ep-saved-pill">✓ Saved</span>}
            <div className="ep-mode-tabs">
              <button className={`ep-mode-tab${mode === 'builder' ? ' ep-mode-tab--active' : ''}`}
                onClick={() => setMode('builder')}>Builder</button>
              <button className={`ep-mode-tab${mode === 'preview' ? ' ep-mode-tab--active' : ''}`}
                onClick={() => setMode('preview')}>Preview</button>
            </div>
            {onClose && <button className="ep-close-btn" onClick={onClose} title="Close estimate">✕</button>}
          </div>
        </div>

        <div className="ep-body">
          {mode === 'builder' ? (
            <BuilderMode
              draft={draft} line0={line0} parts={parts} totals={totals}
              lines={lines} lineIdx={lineIdx}
              onSelectLine={(i) => { setActiveLineIdx(i); setAddLaborOpen(false); setEditLaborId(null); }}
              onAddOperation={handleAddOperation}
              onRemoveOperation={handleRemoveOperation}
              findings={findings} repairs={repairs} canEdit={canModify} canAct={canEdit}
              defaultLaborRate={defaultLaborRate}
              catalogItems={catalogItems}
              addLaborOpen={addLaborOpen}
              setAddLaborOpen={setAddLaborOpen}
              laborForm={laborForm} setLaborForm={setLaborForm}
              editLaborId={editLaborId} setEditLaborId={setEditLaborId}
              onUpdateLine={updateLine} onUpdateDraft={updateDraft}
              onAddLabor={handleAddLabor} onDeleteLabor={handleDeleteLabor} onEditLabor={handleEditLabor}
              onTogglePart={handleTogglePart}
              onSave={() => doSave()} saving={saving}
              findingOpen={findingOpen} setFindingOpen={setFindingOpen}
              repairOpen={repairOpen} setRepairOpen={setRepairOpen}
              approvalMode={approvalMode} setApprovalMode={setApprovalMode}
              approvalForm={approvalForm} setApprovalForm={setApprovalForm}
              declineMode={declineMode} setDeclineMode={setDeclineMode}
              declineReason={declineReason} setDeclineReason={setDeclineReason}
              paymentMode={paymentMode} setPaymentMode={setPaymentMode}
              paymentForm={paymentForm} setPaymentForm={setPaymentForm}
              onRecordPayment={handleRecordPayment}
              sentLink={sentLink}
              onRequestApproval={handleRequestApproval}
              onApprove={handleApprove} onDecline={handleDecline}
              onCreateInvoice={handleCreateInvoice}
              onFinalizeInvoice={handleFinalizeInvoice}
              onRevertInvoice={handleRevertInvoice}
              discountMode={discountMode} setDiscountMode={setDiscountMode}
              discountForm={discountForm} setDiscountForm={setDiscountForm}
              onSaveDiscount={handleSaveDiscount}
              onEmailInvoice={handleEmailInvoice}
              onExportQbo={() => setExportOpen(open => !open)}
              exportOpen={exportOpen}
              onDownloadQbo={handleDownloadQbo}
              onCopyQbo={handleCopyQbo}
              exportNotes={exportOpen ? qboExportNotes(pricedInvoice(), truck, settings) : []}
            />
          ) : (
            <PreviewMode
              draft={draft} parts={parts}
              totals={totals} truck={truck} settings={settings}
            />
          )}
        </div>

      </div>
    </div>
  );
}

// Same component, rendered inline (inline=true) — the money rail's Estimate
// card promotes this out of the modal overlay per the Phase 1 job-detail rebuild.
export { EstimatePanel as EstimateEditor };
